import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import CreateAlertModal from '../components/alerts/CreateAlertModal'
import { Alert, defaultAlert, useApplicationSuiteContext } from './ApplicationSuiteProvider'
import { collection, getCountFromServer, query, where } from 'firebase/firestore'
import { firebase_firestore, firebase_functions } from '../common/firebaseConfig'
import { useHttpsCallable } from 'react-firebase-hooks/functions'
import BacktestRunDialog from '../components/backtests/BacktestRunDialog'
import PhoneValidationDialog from '../components/dialogs/PhoneValidationDialog'
import setNestedValue from 'lodash.set'
import OkayDialog from '../app/builder/components/sidebar/patternInput/OkayDialog'
import TradingAccountDialog from '../components/dialogs/TradingAccountDialog'
import MetaApiAccountsDialog from '../components/dialogs/MetaApiAccountsDialog'
import ReconnectMetaApiAccountDialog from '../components/dialogs/ReconnectMetaApiAccountDialog'

type ModalContextType = {
  isCreateAlertModalOpen: boolean
  setIsCreateAlertModalOpen: React.Dispatch<React.SetStateAction<boolean>>
  setDefaultAlert: React.Dispatch<React.SetStateAction<Alert>>
  isBacktestRunDialogOpen: boolean
  setIsBacktestRunDialogOpen: React.Dispatch<React.SetStateAction<boolean>>
  isPhoneValidationDialogOpen: boolean
  setIsPhoneValidationDialogOpen: React.Dispatch<React.SetStateAction<boolean>>
  setIsOkayDialogOpen: React.Dispatch<
    React.SetStateAction<{ title: string; description: string } | undefined>
  >
  isTradingAccountDialogOpen: boolean
  setIsTradingAccountDialogOpen: React.Dispatch<React.SetStateAction<boolean>>
  isMetaApiAccountsDialogOpen: boolean
  setIsMetaApiAccountsDialogOpen: React.Dispatch<React.SetStateAction<boolean>>
  isReconnectMetaApiDialogOpen: boolean
  setIsReconnectMetaApiDialogOpen: React.Dispatch<React.SetStateAction<boolean>>
}

const ModalContext = createContext<ModalContextType | undefined>(undefined)

const RECONNECT_DISMISS_KEY = 'metaApiReconnectDismissedAt'
const RECONNECT_DISMISS_MS = 24 * 60 * 60 * 1000

type ModalProviderProps = {
  children: ReactNode
}

function ModalProvider({ children }: ModalProviderProps) {
  const {
    usermeta,
    alerts,
    isCreateAlertModalOpen,
    setIsCreateAlertModalOpen,
    setCloseBacktestDialog,
  } = useApplicationSuiteContext()

  const [alert, setDefaultAlert] = useState<Alert>(defaultAlert)
  const [isBacktestRunDialogOpen, setIsBacktestRunDialogOpen] = useState(false)
  const [isPhoneValidationDialogOpen, setIsPhoneValidationDialogOpen] = useState(false)
  const [isOkayDialogOpen, setIsOkayDialogOpen] = useState<{ title: string; description: string }>()
  const [isTradingAccountDialogOpen, setIsTradingAccountDialogOpen] = useState(false)
  const [isMetaApiAccountsDialogOpen, setIsMetaApiAccountsDialogOpen] = useState(false)
  const [isReconnectMetaApiDialogOpen, setIsReconnectMetaApiDialogOpen] = useState(false)

  const [getStatusesFunc] = useHttpsCallable(firebase_functions, 'getMetaApiAccountStatuses')
  const hasFetchedStatuses = useRef(false)

  const accountIdsWithActivePlans = useMemo(() => {
    const ids = new Set<string>()
    alerts.forEach((a: any) => {
      if (a.trading?.accountId) ids.add(a.trading.accountId)
    })
    return ids
  }, [alerts])

  const undeployedAccounts = useMemo(
    () =>
      (usermeta?.metaapi?.accounts || []).filter(
        (a: any) => a.deploymentState === 'UNDEPLOYED' && accountIdsWithActivePlans.has(a.accountId)
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [usermeta?.metaapi?.accounts, accountIdsWithActivePlans]
  )

  useEffect(() => {
    const accounts = usermeta?.metaapi?.accounts || []
    if (accounts.length > 0 && !hasFetchedStatuses.current) {
      hasFetchedStatuses.current = true
      getStatusesFunc({})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usermeta?.metaapi?.accounts?.length])

  useEffect(() => {
    if (undeployedAccounts.length === 0) {
      localStorage.removeItem(RECONNECT_DISMISS_KEY)
      setIsReconnectMetaApiDialogOpen(false)
      return
    }
    const dismissedAt = Number(localStorage.getItem(RECONNECT_DISMISS_KEY) || 0)
    const isDismissActive = dismissedAt > 0 && Date.now() - dismissedAt < RECONNECT_DISMISS_MS
    if (!isDismissActive) {
      setIsReconnectMetaApiDialogOpen(true)
    }
  }, [undeployedAccounts.length])

  useEffect(() => {
    getCountFromServer(
      query(collection(firebase_firestore, `tradingPlans`), where('user.gcid', '==', usermeta.gcid))
    ).then((snapshot: any) => setIsCreateAlertModalOpen(snapshot.data().count === 0))
  }, [usermeta.gcid, setIsCreateAlertModalOpen])

  useEffect(() => {
    if (!isCreateAlertModalOpen && !isTradingAccountDialogOpen) {
      setDefaultAlert(defaultAlert)
    }
  }, [isCreateAlertModalOpen, isTradingAccountDialogOpen])

  // Register the close backtest dialog function
  useEffect(() => {
    setCloseBacktestDialog(() => () => setIsBacktestRunDialogOpen(false))
  }, [setCloseBacktestDialog])

  const handleAlertKeyChange = (key: string, value: any) => {
    setDefaultAlert((prev) => setNestedValue({ ...prev }, key, value))
  }

  const handleReconnectClose = () => {
    setIsReconnectMetaApiDialogOpen(false)
  }

  const handleRemindLater = () => {
    localStorage.setItem(RECONNECT_DISMISS_KEY, String(Date.now()))
    setIsReconnectMetaApiDialogOpen(false)
  }

  return (
    <ModalContext.Provider
      value={{
        isCreateAlertModalOpen,
        setIsCreateAlertModalOpen,
        setDefaultAlert,
        isBacktestRunDialogOpen,
        setIsBacktestRunDialogOpen,
        isPhoneValidationDialogOpen,
        setIsPhoneValidationDialogOpen,
        setIsOkayDialogOpen,
        isTradingAccountDialogOpen,
        setIsTradingAccountDialogOpen,
        isMetaApiAccountsDialogOpen,
        setIsMetaApiAccountsDialogOpen,
        isReconnectMetaApiDialogOpen,
        setIsReconnectMetaApiDialogOpen,
      }}
    >
      {children}
      {isCreateAlertModalOpen && <CreateAlertModal alert={alert} />}
      {isBacktestRunDialogOpen && <BacktestRunDialog />}
      {isPhoneValidationDialogOpen && (
        <PhoneValidationDialog
          open={isPhoneValidationDialogOpen}
          onClose={() => setIsPhoneValidationDialogOpen(false)}
          alert={alert}
          onAlertKeyChange={handleAlertKeyChange}
        />
      )}
      {isOkayDialogOpen && (
        <OkayDialog
          open={!!isOkayDialogOpen}
          title={isOkayDialogOpen.title}
          description={isOkayDialogOpen.description}
          onClose={() => setIsOkayDialogOpen(undefined)}
          onOkay={() => setIsOkayDialogOpen(undefined)}
        />
      )}
      {isTradingAccountDialogOpen && (
        <TradingAccountDialog
          open={isTradingAccountDialogOpen}
          onClose={() => setIsTradingAccountDialogOpen(false)}
          alert={alert}
        />
      )}
      {isMetaApiAccountsDialogOpen && (
        <MetaApiAccountsDialog
          open={isMetaApiAccountsDialogOpen}
          onClose={() => setIsMetaApiAccountsDialogOpen(false)}
        />
      )}
      {isReconnectMetaApiDialogOpen && undeployedAccounts.length > 0 && (
        <ReconnectMetaApiAccountDialog
          open={isReconnectMetaApiDialogOpen}
          accounts={undeployedAccounts}
          onClose={handleReconnectClose}
          onRemindLater={handleRemindLater}
        />
      )}
    </ModalContext.Provider>
  )
}

function useModalContext(): ModalContextType {
  const context = useContext(ModalContext)
  if (!context) {
    throw new Error('useModal must be used within an ModalProvider')
  }
  return context
}

export { ModalProvider, useModalContext }
